/*
 * Environment pill — reads /api/config (cached at app start by api.config())
 * and renders Production / Staging / Demo / Development with tone-coded
 * colour. Hidden in Production by default since the badge would just be
 * visual noise on the live deployment; show it for anything else so
 * operators can never confuse the environments.
 */

import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";

type EnvTone = "production" | "staging" | "demo" | "development";
interface EnvInfo { profile: string; label: string; tone: EnvTone; nodeEnv: string }

let cachedEnv: EnvInfo | null = null;
let inflight: Promise<EnvInfo | null> | null = null;

async function loadEnv(): Promise<EnvInfo | null> {
  if (cachedEnv) return cachedEnv;
  if (inflight) return inflight;
  inflight = api.config()
    .then((cfg) => {
      const env = (cfg as { environment?: EnvInfo }).environment ?? null;
      if (env) cachedEnv = env;
      return env;
    })
    .catch((e) => {
      // Silently degrade — the badge is observability, not auth-critical.
      if (!(e instanceof ApiError)) console.debug("env-badge load failed", e);
      return null;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export default function EnvironmentBadge({ alwaysShow = false }: { alwaysShow?: boolean }) {
  const [env, setEnv] = useState<EnvInfo | null>(cachedEnv);
  useEffect(() => {
    let cancelled = false;
    void loadEnv().then((e) => { if (!cancelled) setEnv(e); });
    return () => { cancelled = true; };
  }, []);
  if (!env) return null;
  // Hide on Production unless caller forces — keeps the live console
  // visually quieter while still always-visible elsewhere.
  if (env.tone === "production" && !alwaysShow) return null;
  return (
    <span
      className={`env-badge env-badge-${env.tone}`}
      title={`Environment: ${env.label} (${env.nodeEnv})`}
      aria-label={`Environment: ${env.label}`}
    >
      <span className="env-badge-dot" aria-hidden />
      {env.label}
    </span>
  );
}
